/**
=========================================================
* Trust Cargo - Order statuses
=========================================================

* The short list of stages an order can be in. Adding and editing both happen
* in a dialog over this list rather than on a page of their own.
*/

import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import { Table, TableCell, TableHead, TableBody, TableRow } from "@mui/material";
import axios from "axios";

// Soft UI Dashboard React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";
import FilterBar, { filterRows } from "components/FilterBar";
import Pagination, { usePagination } from "components/Pagination";
import IdCell, { IdHead } from "components/IdCell";
import AddDialog from "components/AddDialog";
import RowActions from "components/RowActions";

// Soft UI Dashboard React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

const API = "https://trust-cargo.co/anas_backend/api";

function Status() {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const fetchData = async () => {
    try {
      const response = await fetch(`${API}/status`);
      const data = await response.json();
      setRows(data.status || []);
    } catch (e) {
      setRows([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, []);

  const filteredRows = filterRows(rows, query);
  const pager = usePagination(filteredRows);

  const removeOne = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete status "${row.name}"?`)) return;

    try {
      await axios.post(
        `${API}/delete_status`,
        { id: row.id },
        { headers: { Accept: "application/json" } }
      );
      await fetchData();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e?.response?.data?.message || "Could not delete");
    }
  };

  const cell = { fontSize: 14 };

  return (
    <DashboardLayout>
      <SuiBox py={1}>
        <SuiBox mb={3}>
          <Card>
            <FilterBar
              title="Status"
              value={query}
              onChange={setQuery}
              count={filteredRows.length}
              total={rows.length}
              placeholder="Search statuses..."
              actions={
                <Button variant="contained" color="primary" onClick={() => setAdding(true)}>
                  New Status
                </Button>
              }
            />

            {loading ? (
              <CargoLoader label="Loading statuses" />
            ) : (
              <>
                <SuiBox sx={{ overflowX: "auto" }}>
                  <Table sx={{ minWidth: 600 }} aria-label="status table">
                    <TableHead>
                      <TableRow>
                        <IdHead />
                        <TableCell align="center">Name</TableCell>
                        <TableCell align="center">Operations</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {pager.pageRows.map((row) => (
                        <TableRow key={row.id}>
                          <IdCell value={row.id} />
                          <TableCell align="center" sx={{ ...cell, fontWeight: 700 }}>
                            {row.name ?? "-"}
                          </TableCell>
                          <TableCell align="center" sx={cell}>
                            <RowActions
                              actions={[
                                {
                                  icon: "edit",
                                  label: "Edit status",
                                  onClick: () => setEditingId(row.id),
                                },
                                {
                                  icon: "delete",
                                  label: "Delete",
                                  tone: "danger",
                                  onClick: () => removeOne(row),
                                },
                              ]}
                            />
                          </TableCell>
                        </TableRow>
                      ))}

                      {filteredRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                            <SuiTypography variant="button" color="text">
                              No statuses
                            </SuiTypography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </SuiBox>
                <Pagination {...pager} />
              </>
            )}
          </Card>
        </SuiBox>
      </SuiBox>

      <AddDialog
        open={adding}
        entity="status"
        onClose={() => setAdding(false)}
        onCreated={fetchData}
      />

      {/* same dialog, loaded with the existing status */}
      <AddDialog
        open={Boolean(editingId)}
        entity="status"
        recordId={editingId}
        onClose={() => setEditingId(null)}
        onCreated={fetchData}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default Status;
